import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashLayout from "../components/DashLayout";
import api, { formatApiErrorDetail } from "../lib/api";
import { Plus, Trash2, ExternalLink, Lock, Activity, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

function StatusDot({ status }) {
    const c = status === "online" ? "bg-[#00FF41] shadow-[0_0_8px_#00FF41]" : "bg-[#52525B]";
    return <span className={`inline-block w-2 h-2 ${c}`} />;
}

export default function Tunnels() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [subdomain, setSubdomain] = useState("");
    const [port, setPort] = useState(3000);
    const [label, setLabel] = useState("");
    const [creating, setCreating] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/tunnels");
            setItems(Array.isArray(data) ? data : (data?.tunnels && Array.isArray(data.tunnels) ? data.tunnels : []));
        } catch (e) {
            toast.error("Failed to load tunnels");
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

    const create = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const { data } = await api.post("/tunnels", { subdomain, local_port: Number(port), label: label || null });
            toast.success(`Tunnel ${data.public_url ? data.public_url.replace("https://", "") : `${subdomain}.synapselink.cloud`} created`);
            setShowCreate(false);
            setSubdomain(""); setLabel(""); setPort(3000);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
        } finally {
            setCreating(false);
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this tunnel? Logs will be wiped.")) return;
        try {
            await api.delete(`/tunnels/${id}`);
            toast.success("Tunnel deleted");
            load();
        } catch {
            toast.error("Failed to delete");
        }
    };

    return (
        <DashLayout
            title="TUNNELS"
            subtitle="dashboard / tunnels"
            action={
                <button onClick={() => setShowCreate(true)} className="tf-btn" data-testid="open-create-tunnel-btn">
                    <Plus className="w-4 h-4" /> NEW TUNNEL
                </button>
            }
        >
            {loading ? (
                <div className="text-[#52525B] text-sm">[ loading tunnels... ]</div>
            ) : !Array.isArray(items) || items.length === 0 ? (
                <div className="tf-card-accent p-12 text-center" data-testid="empty-tunnels">
                    <Activity className="w-12 h-12 text-[#00FF41] mx-auto mb-4" strokeWidth={1.5} />
                    <h3 className="heading-md mb-2">NO TUNNELS YET</h3>
                    <p className="text-[#A1A1AA] mb-6">Reserve a subdomain to forward your local port to a persistent public URL.</p>
                    <button onClick={() => setShowCreate(true)} className="tf-btn inline-flex" data-testid="empty-create-btn">
                        <Plus className="w-4 h-4" /> CREATE FIRST TUNNEL
                    </button>
                </div>
            ) : (
                <div className="border border-[#27272A]" data-testid="tunnels-list">
                    <div className="hidden md:grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-6 px-5 py-3 border-b border-[#27272A] bg-[#0A0A0A] label-mono">
                        <div>Status</div>
                        <div>Subdomain · Label</div>
                        <div>Port</div>
                        <div>Requests</div>
                        <div>Auth</div>
                        <div className="text-right">Actions</div>
                    </div>
                    {Array.isArray(items) && items.map((t) => (
                        <div key={t.id} className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto_auto_auto_auto] gap-3 md:gap-6 px-5 py-4 border-b last:border-b-0 border-[#27272A] items-center hover:bg-[#0A0A0A]" data-testid={`tunnel-row-${t.subdomain}`}>
                            <div className="flex items-center gap-2">
                                <StatusDot status={t.status} />
                                <span className="text-xs uppercase text-[#A1A1AA] md:hidden">{t.status}</span>
                            </div>
                            <div className="min-w-0">
                                <Link to={`/app/tunnels/${t.id}`} className="font-mono text-[#00FF41] hover:underline truncate block" data-testid={`tunnel-link-${t.subdomain}`}>
                                    {t.public_url ? t.public_url.replace("https://", "") : `${t.subdomain}.synapselink.cloud`}
                                </Link>
                                <div className="text-xs text-[#52525B] truncate">{t.label || "—"}</div>
                            </div>
                            <div className="font-mono text-sm text-[#A1A1AA]">:{t.local_port}</div>
                            <div className="font-mono text-sm text-[#FAFAFA]">{t.request_count}</div>
                            <div>
                                {t.password_protect ? (
                                    <span className="text-[#FF9900] flex items-center gap-1 text-xs uppercase tracking-wider"><Lock className="w-3 h-3" />Locked</span>
                                ) : (
                                    <span className="text-[#52525B] text-xs uppercase tracking-wider">Open</span>
                                )}
                            </div>
                            <div className="flex justify-end gap-2">
                                <Link to={`/app/tunnels/${t.id}`} className="tf-btn tf-btn-ghost !py-1.5 !px-3" data-testid={`inspect-${t.subdomain}`}>
                                    <ExternalLink className="w-3.5 h-3.5" />Inspect
                                </Link>
                                <button onClick={() => remove(t.id)} className="tf-btn tf-btn-danger !py-1.5 !px-3" data-testid={`delete-${t.subdomain}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showCreate && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setShowCreate(false)} data-testid="create-tunnel-modal">
                    <div className="tf-card-accent w-full max-w-lg p-8 relative" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setShowCreate(false)} className="absolute top-4 right-4 text-[#52525B] hover:text-[#FAFAFA]" data-testid="close-modal">
                            <X className="w-5 h-5" />
                        </button>
                        <div className="label-mono text-[#00FF41] mb-2">// new tunnel</div>
                        <h3 className="heading-md mb-6">RESERVE A SUBDOMAIN</h3>
                        <form onSubmit={create} className="space-y-6">
                            <div>
                                <div className="label-mono mb-1">SUBDOMAIN</div>
                                <div className={`flex items-center border-b-2 ${user?.plan === "free" ? "border-[#27272A]/50 bg-[#0A0A0A]/50 cursor-not-allowed" : "border-[#27272A] focus-within:border-[#00FF41]"}`}>
                                    <span className="text-[#52525B] mr-2">{">"}</span>
                                    <input
                                        className="bg-transparent flex-1 py-2 outline-none font-mono text-[#FAFAFA] disabled:text-[#52525B] disabled:cursor-not-allowed"
                                        placeholder={user?.plan === "free" ? "[ Auto-generated random subdomain ]" : "my-project"}
                                        value={user?.plan === "free" ? "" : subdomain}
                                        onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                                        required={user?.plan !== "free"}
                                        disabled={user?.plan === "free"}
                                        minLength={user?.plan !== "free" ? 3 : undefined}
                                        data-testid="create-subdomain-input"
                                    />
                                    <span className="text-[#52525B] font-mono text-sm pr-2">.{user?.tunnel_domain || "synapselink.cloud"}</span>
                                </div>
                            </div>
                            <div>
                                <div className="label-mono mb-1">LOCAL PORT</div>
                                <div className="flex items-center border-b-2 border-[#27272A] focus-within:border-[#00FF41]">
                                    <span className="text-[#52525B] mr-2">{">"}</span>
                                    <input type="number" min="1" max="65535" className="bg-transparent flex-1 py-2 outline-none font-mono text-[#FAFAFA]" value={port} onChange={(e) => setPort(e.target.value)} required data-testid="create-port-input" />
                                </div>
                            </div>
                            <div>
                                <div className="label-mono mb-1">LABEL · OPTIONAL</div>
                                <div className="flex items-center border-b-2 border-[#27272A] focus-within:border-[#00FF41]">
                                    <span className="text-[#52525B] mr-2">{">"}</span>
                                    <input className="bg-transparent flex-1 py-2 outline-none font-mono text-[#FAFAFA]" placeholder="Stripe webhooks" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="create-label-input" />
                                </div>
                            </div>
                            <button type="submit" disabled={creating} className="tf-btn w-full justify-center" data-testid="create-tunnel-submit">
                                {creating ? "RESERVING..." : "RESERVE SUBDOMAIN"}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </DashLayout>
    );
}
